import { INVESTMENTS } from '../../data/investments';
import { calculateLoanPayment } from '../../utils/format';
import { generateId } from '../helpers';
import { tryPromotePhase } from '../helpers/phaseProgression';
import { getDepotEffectiveInvestments } from '../../data/depotPhases';

export const createFinanceSlice = (set, get) => ({
  credits: [],
  activeInvestments: {},
  /**
   * 08/02/2026 — Q2 Phase 3 user prod : investissements PAR DÉPÔT.
   * Shape : `{ [depotId]: { [investmentId]: true } }`. Création d'un nouveau
   * dépôt = entrée vide, le joueur repaye chaque évolution localement.
   * Les catégories non rattachables à un dépôt (Bureau, Marketing, RH…)
   * vont sur 'depot-hq' par convention.
   */
  depotInvestments: { 'depot-hq': {} },
  /**
   * 28/05/2026 v2 — track the day each investment was activated so the
   * extended depreciation engine (utils/capexDepreciation.js) can compute
   * the elapsed life and the residual VNC for every CAPEX category
   * (depots, office, software, website). Pre-existing investments
   * default to day 0 (they look fully amortized over time anyway).
   */
  investmentPurchaseDays: {},
  /**
   * 29/05/2026 — lifetime cumulative CAPEX spent (€). Used by the
   * leaseAidSlice yearly snapshot to detect when the player passes the
   * 50 000 € threshold for the CFE exoneration regional aid.
   */
  lifetimeCapexSpent: 0,
  monthlyRevenues: 0,
  monthlyExpenses: 0,
  lastDayRevenue: 0,
  lastDayExpenses: 0,

  activateInvestment: (investmentId) => {
    const state = get();
    const inv = INVESTMENTS.find(i => i.id === investmentId);
    if (!inv || state.activeInvestments[investmentId]) return;
    if (state.treasury < inv.costBuy) return;
    // 04/02/2026 — Q1 Bug Dépôt : graphe de dépendance STRICT.
    // L'UI affiche déjà un cadenas quand `conditionCheck` est faux, mais
    // toute mutation directe du store (test, script, événement) doit
    // également respecter le prérequis (défense en profondeur). On
    // refuse silencieusement si la condition n'est pas satisfaite.
    if (typeof inv.conditionCheck === 'function' && !inv.conditionCheck(state)) {
      return;
    }

    // 08/02/2026 — Q2 Phase 3 user prod : on rattache l'investissement au
    // dépôt courant via state.depotInvestments. Source de vérité par dépôt
    // pour les évolutions « Dépôt » et « Énergie ». Les autres catégories
    // (Bureau, Marketing, RH…) restent globales (rattachées au QG par
    // défaut). Création d'un nouveau dépôt = depotInvestments[newDepotId]
    // est vide, le joueur repaye chaque évolution localement.
    const targetDepotId = state.activeDepotId || 'depot-hq';
    const isPerDepot = inv.category === 'Dépôt' || inv.category === 'Énergie'; // i18n-keep
    const newDepotInvestments = { ...(state.depotInvestments || {}) };
    if (isPerDepot) {
      newDepotInvestments[targetDepotId] = {
        ...(newDepotInvestments[targetDepotId] || {}),
        [investmentId]: true,
      };
    } else {
      // Catégorie globale → on stocke quand même côté QG pour traçabilité.
      newDepotInvestments['depot-hq'] = {
        ...(newDepotInvestments['depot-hq'] || {}),
        [investmentId]: true,
      };
    }

    set({
      activeInvestments: { ...state.activeInvestments, [investmentId]: true },
      depotInvestments: newDepotInvestments,
      investmentPurchaseDays: {
        ...(state.investmentPurchaseDays || {}),
        [investmentId]: state.totalDays || 0,
      },
      lifetimeCapexSpent: (state.lifetimeCapexSpent || 0) + (inv.costBuy || 0),
      treasury: state.treasury - inv.costBuy,
    });

    // 18/06/2026 — audit TVA user : CAPEX investissement → TVA déductible 20 %.
    if (typeof get().recordDeductibleTva === 'function') {
      get().recordDeductibleTva(inv.costBuy || 0, 'investments');
    }

    // 08/02/2026 — Refonte phases (Q2). Certains investissements clés
    // déclenchent une bascule de phase action-based + une vidéo Sora 2.
    if (investmentId === 'inv-depot-01') {
      // P1 → P2 : Agrandissement dépôt (8 k€).
      tryPromotePhase(get, set, 2, 'Agrandissement du dépôt principal');
    } else if (investmentId === 'inv-depot-06') {
      // P2 → P3 : Extension logistique (+100 km de rayon).
      tryPromotePhase(get, set, 3, 'Extension logistique du QG (+100 km)');
    } else if (investmentId === 'inv-office-01') {
      // Milestone vidéo dédiée office-upgrade (pas de bascule de phase).
      if (typeof get().triggerMilestoneVideo === 'function') {
        try {
          get().triggerMilestoneVideo({
            kind: 'office-upgrade',
            reason: 'Siège social inauguré',
          });
        } catch (err) { console.warn('[financeSlice] office video trigger failed:', err); }
      }
    } else if (investmentId === 'inv-it-backup-01') {
      // 08/02/2026 — Q2 Lot C : Sauvegarde + audit IT.
      //   +2 réputation immédiate (signal sérieux aux clients).
      //   Pas d'effet récurrent ici (Lot D wirera la réduction des risques admin).
      const after = get();
      set({ reputation: Math.max(0, Math.min(100, (after.reputation || 0) + 2)) });
    } else if (investmentId === 'inv-security-01') {
      // 08/02/2026 — Q2 Lot C : Vidéosurveillance / gardiennage.
      //   +2 réputation immédiate.
      //   +3 satisfaction sur tous les chauffeurs (équipe rassurée).
      const after = get();
      set({
        reputation: Math.max(0, Math.min(100, (after.reputation || 0) + 2)),
        drivers: (after.drivers || []).map(d => ({
          ...d,
          satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + 3)),
        })),
      });
    }
  },

  deactivateInvestment: (investmentId) => {
    const state = get();
    const newInvestments = { ...state.activeInvestments };
    delete newInvestments[investmentId];
    const newDays = { ...(state.investmentPurchaseDays || {}) };
    delete newDays[investmentId];
    // Aussi nettoyer depotInvestments (toutes les références à cet inv).
    const newDepotInv = { ...(state.depotInvestments || {}) };
    Object.keys(newDepotInv).forEach(did => {
      if (newDepotInv[did]?.[investmentId]) {
        const copy = { ...newDepotInv[did] };
        delete copy[investmentId];
        newDepotInv[did] = copy;
      }
    });
    set({ activeInvestments: newInvestments, depotInvestments: newDepotInv, investmentPurchaseDays: newDays });
  },

  /**
   * 08/02/2026 — Q2 Phase 3 user prod : permet d'acheter le MÊME
   * investissement pour un AUTRE dépôt. Bypasses la garde globale
   * `activeInvestments[id]`. Vérifie `depotInvestments[depotId][id]`.
   * Réservé aux catégories « Dépôt » et « Énergie » (le user voulait
   * « repayer chaque évolution » pour un nouveau dépôt). Les autres
   * catégories restent globales (1 seul achat possible).
   *
   * 13/07/2026 — Progression unifiée des dépôts :
   *   - les prérequis (chaîne de dépendances) sont évalués sur les
   *     investissements DE CE DÉPÔT (proxy state), plus sur le global ;
   *   - l'achat est reflété sur `depot.investments` (source des effets
   *     par dépôt : rayon, loyer, atelier, pompe, visuel brandé) ;
   *   - parité legacy : quai (+5 capacité) et extension +100 km
   *     (promotion phase 3) conservent leurs effets.
   */
  activateDepotInvestment: (investmentId, depotId = null) => {
    const state = get();
    const inv = INVESTMENTS.find(i => i.id === investmentId);
    if (!inv) return { ok: false, reason: 'unknown_investment' };
    const targetDepotId = depotId || state.activeDepotId || 'depot-hq';
    // Only per-depot categories can be re-purchased per site.
    const isPerDepot = inv.category === 'Dépôt' || inv.category === 'Énergie'; // i18n-keep
    if (!isPerDepot) {
      // Catégorie globale → on délègue à activateInvestment classique.
      get().activateInvestment(investmentId);
      return { ok: !!get().activeInvestments[investmentId], reason: 'fallback_global' };
    }
    const depot = (state.ownedDepots || []).find(d => d.id === targetDepotId);
    const isSecondary = !!depot && !depot.isHeadquarters;
    const depotMap = isSecondary
      ? getDepotEffectiveInvestments(depot)
      : ((state.depotInvestments || {})[targetDepotId] || {});
    if (depotMap[investmentId]) return { ok: false, reason: 'already_active_at_depot' };
    if (state.treasury < inv.costBuy) return { ok: false, reason: 'insufficient_funds', cost: inv.costBuy };
    // Prérequis évalués sur les investissements du dépôt cible (les
    // conditions globales — CA, flotte, jours — restent lues sur le state).
    const checkState = isSecondary ? { ...state, activeInvestments: depotMap } : state;
    if (typeof inv.conditionCheck === 'function' && !inv.conditionCheck(checkState)) {
      return { ok: false, reason: 'condition_not_met' };
    }
    const newDepotInv = {
      ...(state.depotInvestments || {}),
      [targetDepotId]: { ...((state.depotInvestments || {})[targetDepotId] || {}), [investmentId]: true },
    };
    // `activeInvestments` reste l'union (≥1 dépôt l'a → globalement actif).
    const newGlobalActive = { ...state.activeInvestments, [investmentId]: true };
    set({
      activeInvestments: newGlobalActive,
      depotInvestments: newDepotInv,
      // Miroir sur l'objet dépôt (lu par depotGeo/computeDailyExpenses/scène).
      ownedDepots: isSecondary ? (state.ownedDepots || []).map(d => d.id === targetDepotId ? {
        ...d,
        investments: { ...(d.investments || {}), [investmentId]: true },
        // Parité legacy `quai` : +5 véhicules de capacité sur le site.
        capacityVehicles: investmentId === 'inv-depot-04'
          ? (d.capacityVehicles || 5) + 5
          : d.capacityVehicles,
      } : d) : state.ownedDepots,
      lifetimeCapexSpent: (state.lifetimeCapexSpent || 0) + (inv.costBuy || 0),
      treasury: state.treasury - inv.costBuy,
      investmentPurchaseDays: {
        ...(state.investmentPurchaseDays || {}),
        [investmentId]: state.investmentPurchaseDays?.[investmentId] || (state.totalDays || 0),
      },
      ...(isSecondary ? {
        notifications: [...(state.notifications || []).slice(-30), {
          id: `depot-inv-${targetDepotId}-${investmentId}`,
          type: 'depot_upgraded',
          title: `Évolution dépôt — ${depot.name || targetDepotId}`,
          message: `Travaux livrés : ${inv.name} (−${(inv.costBuy || 0).toLocaleString('fr-FR')} €). Le site s'agrandit — le loyer suit la taille du terrain.`,
          day: state.totalDays,
          read: false,
        }],
      } : {}),
    });
    // 18/06/2026 — audit TVA user : CAPEX investissement → TVA déductible 20 %.
    if (typeof get().recordDeductibleTva === 'function') {
      get().recordDeductibleTva(inv.costBuy || 0, 'investments');
    }
    // Parité legacy `radiusExtension` : étendre le rayon d'un dépôt à
    // 350 km lui donne une vocation nationale → promotion phase 3.
    if (isSecondary && investmentId === 'inv-depot-06') {
      tryPromotePhase(get, set, 3, `Extension logistique (+100 km) sur ${depot.name || targetDepotId}`);
    }
    return { ok: true, depotId: targetDepotId };
  },

  takeLoan: (amount, durationMonths, rate = 0.055) => {
    const state = get();
    const payment = calculateLoanPayment(amount, rate, durationMonths);
    const loan = {
      id: generateId(),
      principal: amount,
      annualRate: rate,
      durationMonths,
      monthlyPayment: payment,
      remainingMonths: durationMonths,
      remainingPrincipal: amount,
    };
    set({
      credits: [...state.credits, loan],
      treasury: state.treasury + amount,
    });
  },
});
